import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, User, Send, CheckCircle2 } from "lucide-react";

const INTERESTS = [
  { id: "diagnostyka", label: "Diagnostyka Zdrowia" },
  { id: "match", label: "Secure Match" },
  { id: "natal", label: "Secure Natal" },
  { id: "ivf", label: "Wsparcie IVF" },
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

export default function ContactForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    interests: [],
    message: "",
    consent: false,
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const toggleInterest = (id) => {
    setForm((f) => ({
      ...f,
      interests: f.interests.includes(id)
        ? f.interests.filter((x) => x !== id)
        : [...f.interests, id],
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Podaj imię, e-mail i telefon.");
      return;
    }
    if (!form.consent) {
      toast.error("Zaakceptuj politykę prywatności.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        interests: form.interests,
        message: form.message,
      });
      setSent(true);
      toast.success("Dziękujemy! Skontaktujemy się z Tobą w ciągu 24 godzin.");
    } catch (err) {
      toast.error("Coś poszło nie tak. Spróbuj ponownie.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      className="relative py-20 lg:py-28 bg-nuuma-grey100 overflow-hidden rounded-[50px] lg:rounded-[99px]"
    >
      {/* Background Image */}
      <div 
        className="absolute inset-0 z-0 opacity-50 pointer-events-none mix-blend-multiply"
        style={{
          backgroundImage: "url('/assets/nuuma-lens-bg-111.webp')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="nuuma-container relative z-10">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          {/* Left: intro */}
          <div className="lg:sticky lg:top-28">
            <span className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-nuuma-secondary mb-3">
              Kontakt
            </span>
            <h2 className="font-display font-bold text-nuuma-primary text-3xl sm:text-4xl lg:text-[2.6rem] leading-[1.15]">
              Porozmawiajmy o{" "}
              <span className="text-[#db8f8e]">
                Twoim zdrowiu
              </span>
            </h2>
            <p className="mt-5 text-nuuma-grey700 text-base leading-relaxed max-w-lg">
              Zostaw kontakt, a nasz ekspert oddzwoni w ciągu 24h. Konsultacja
              jest bezpłatna i niezobowiązująca.
            </p>

            <div className="mt-8 space-y-4">
              <a
                href="tel:+48221004239"
                data-testid="contact-phone"
                className="flex items-center gap-4 p-4 rounded-nuuma-m bg-white border border-nuuma-grey200 hover:border-nuuma-secondary transition-colors"
              >
                <div className="h-10 w-10 rounded-full bg-nuuma-primary/5 flex items-center justify-center">
                  <Phone className="h-4 w-4 text-nuuma-primary" />
                </div>
                <div>
                  <div className="text-xs text-nuuma-grey500">Telefon</div>
                  <div className="font-semibold text-nuuma-primary">
                    +48 22 100 42 39
                  </div>
                </div>
              </a>
              <a
                href="mailto:info@nuumagenetics.pl"
                data-testid="contact-email"
                className="flex items-center gap-4 p-4 rounded-nuuma-m bg-white border border-nuuma-grey200 hover:border-nuuma-secondary transition-colors"
              >
                <div className="h-10 w-10 rounded-full bg-nuuma-primary/5 flex items-center justify-center">
                  <Mail className="h-4 w-4 text-nuuma-primary" />
                </div>
                <div>
                  <div className="text-xs text-nuuma-grey500">E-mail</div>
                  <div className="font-semibold text-nuuma-primary">
                    info@nuumagenetics.pl
                  </div>
                </div>
              </a>
            </div>
          </div>

          {/* Right: form */}
          <div
            data-testid="contact-form-card"
            className="bg-white rounded-nuuma-l p-7 sm:p-10 shadow-nuuma-m"
          >
            {sent ? (
              <div
                data-testid="contact-success"
                className="text-center py-12 animate-fade-up"
              >
                <div className="h-16 w-16 mx-auto rounded-full bg-nuuma-success/10 flex items-center justify-center mb-5">
                  <CheckCircle2 className="h-8 w-8 text-nuuma-success" />
                </div>
                <h3 className="font-display font-bold text-nuuma-primary text-2xl">
                  Dziękujemy!
                </h3>
                <p className="mt-3 text-nuuma-grey700">
                  Twoja wiadomość została wysłana. Skontaktujemy się z Tobą w ciągu 24 godzin.
                </p>
                <button
                  data-testid="contact-reset"
                  onClick={() => {
                    setSent(false);
                    setForm({
                      name: "",
                      email: "",
                      phone: "",
                      interests: [],
                      message: "",
                      consent: false,
                    });
                  }}
                  className="mt-7 btn-pill btn-outline-nuuma"
                >
                  Wyślij kolejne zapytanie
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <FormField
                    icon={User}
                    id="name"
                    label="Imię i nazwisko *"
                    value={form.name}
                    onChange={(v) => setForm({ ...form, name: v })}
                  />
                  <FormField
                    icon={Mail}
                    id="email"
                    type="email"
                    label="E-mail *"
                    value={form.email}
                    onChange={(v) => setForm({ ...form, email: v })}
                  />
                </div>
                <FormField
                  icon={Phone}
                  id="phone"
                  type="tel"
                  label="Telefon *"
                  value={form.phone}
                  onChange={(v) => setForm({ ...form, phone: v })}
                />

                <div>
                  <label className="block text-xs font-semibold text-nuuma-primary uppercase tracking-wider mb-3">
                    Interesuje mnie
                  </label>
                  <div className="grid sm:grid-cols-2 gap-2.5">
                    {INTERESTS.map((i) => {
                      const checked = form.interests.includes(i.id);
                      return (
                        <label
                          key={i.id}
                          data-testid={`interest-${i.id}`}
                          className={`flex items-center gap-3 px-4 py-3 rounded-nuuma-s border cursor-pointer transition-all ${
                            checked
                              ? "border-nuuma-secondary bg-nuuma-secondary/5"
                              : "border-nuuma-grey200 bg-nuuma-grey100 hover:border-nuuma-primary/30"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleInterest(i.id)}
                            className="sr-only"
                          />
                          <span
                            className={`h-4 w-4 rounded border-2 flex items-center justify-center transition-colors ${
                              checked
                                ? "border-nuuma-secondary bg-nuuma-secondary"
                                : "border-nuuma-grey500"
                            }`}
                          >
                            {checked && (
                              <svg
                                className="h-2.5 w-2.5 text-white"
                                viewBox="0 0 12 12"
                                fill="none"
                              >
                                <path
                                  d="M2 6l3 3 5-6"
                                  stroke="currentColor"
                                  strokeWidth="2.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            )}
                          </span>
                          <span className="text-sm text-nuuma-grey900">
                            {i.label}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block text-xs font-semibold text-nuuma-primary uppercase tracking-wider mb-2"
                  >
                    Dodatkowe informacje
                  </label>
                  <textarea
                    data-testid="contact-message"
                    id="message"
                    rows={4}
                    value={form.message}
                    onChange={(e) =>
                      setForm({ ...form, message: e.target.value })
                    }
                    className="w-full px-4 py-3 rounded-nuuma-s bg-nuuma-grey100 border border-nuuma-grey200 text-nuuma-grey900 text-sm focus:outline-none focus:border-nuuma-primary focus:bg-white transition-colors"
                    placeholder="Opisz, czego potrzebujesz..."
                  />
                </div>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="contact-consent"
                    checked={form.consent}
                    onChange={(e) =>
                      setForm({ ...form, consent: e.target.checked })
                    }
                    className="mt-1 h-4 w-4 rounded border-nuuma-grey500 text-nuuma-secondary focus:ring-nuuma-secondary"
                  />
                  <span className="text-xs text-nuuma-grey700 leading-relaxed">
                    Akceptuję{" "}
                    <Link to="/polityka-prywatnosci" className="text-nuuma-primary underline">
                      politykę prywatności
                    </Link>{" "}
                    i zgadzam się na kontakt w sprawie mojego zapytania.
                  </span>
                </label>

                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={loading}
                  className="btn-pill btn-primary-nuuma w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    "Wysyłanie..."
                  ) : (
                    <>
                      Wyślij zapytanie
                      <Send className="h-4 w-4" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function FormField({ icon: Icon, id, label, type = "text", value, onChange }) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-xs font-semibold text-nuuma-primary uppercase tracking-wider mb-2"
      >
        {label}
      </label>
      <div className="relative">
        {Icon && (
          <Icon className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-nuuma-grey500" />
        )}
        <input
          data-testid={`contact-field-${id}`}
          id={id}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`w-full ${
            Icon ? "pl-10" : "pl-4"
          } pr-4 py-3 rounded-nuuma-s bg-nuuma-grey100 border border-nuuma-grey200 text-nuuma-grey900 text-sm focus:outline-none focus:border-nuuma-primary focus:bg-white transition-colors`}
        />
      </div>
    </div>
  );
}
